import { useSignup } from "./hooks/useSignup";
import Text from "@/components/Text";
import PhoneNumberDialog from "./components/PhoneNumberDialog";
import { Box, CircularProgress, InputAdornment } from "@mui/material";
import Spacer from "@/components/Spacer";
import TextField from "@/components/TextField";
import Checkbox from "@/components/Checkbox";
import Button from "@/components/Button";
import PhoneInput from "@/components/PhoneInput";
import GenderSelect from "@/components/GenderSelect";
import StateSelect from "@/components/StateSelect";
import CitySelect from "@/components/CitySelect";
import DateInput from "@/components/DateInput";
import { Gender } from "@/services/auth/types/auth.types";
import Dialog from "@/components/Dialog";
import AuthWrapper from "@/layout/AuthWrapper";
import Divider from "@/components/Divider";
import Theme from "@/utils/theme";
import { useReality } from "@/context/RealityContext";
import { realityInfo } from "@/utils/realityInfo";
import SignupStepper from "./components/SignupStepper";
import { getTermsOfUseFile } from "@/utils/get-terms-of-use-file";
import { OtpCodeInput } from "./styles";
import ResendCodeButton from "../../components/ResendCodeButton";
import { useNavigate } from "react-router-dom";
import CircleCheck from "@/ui/icons/CircleCheck";
import MaskedInput from "@/components/MaskedInput";
import InfoIcon from "@/ui/icons/InfoIcon";
import { Tooltip } from "@/components/Tooltip";

export default function SignUp() {
  const navigate = useNavigate();

  const {
    skipPhoneIntro,
    isOpenPhoneNumberDialog,
    setIsOpenPhoneNumberDialog,
    isOpenAlreadyRegistered,
    isOpenChooseValidation,
    isOpenSignUp,
    isOpenBlacklistedModal,
    isOpenFinishSignUp,
    onSendSmsCode,
    goBack,
    goToWebsite,
    goToSignIn,
    onValidatePhone,
    closeSignupDialog,
    onFixPhoneNumber,
    isLoadingButton,
    currentStep,
    setCurrentStep,

    email,
    setEmail,
    emailError,
    emailConfirmation,
    setEmailConfirmation,
    emailConfirmationError,
    name,
    setName,
    nameError,
    phoneNumber,
    setPhoneNumber,
    phoneNumberError,
    otp,
    setOtp,
    isInvalidCode,
    isFinishingSignUp,
    smsTimerValue,
    birthDate,
    setBirthDate,
    birthDateError,
    gender,
    setGender,
    selectedState,
    setSelectedState,
    selectedCity,
    setSelectedCity,

    confirmWorker,
    setConfirmWorker,
    confirmTerms,
    setConfirmTerms,

    resaleCode,
    setResaleCode,
    resaleCodeAnswer,
    setResaleCodeAnswer,
    isVisibleResaleCodeTooltip,
    toggleIsVisibleResaleCodeTooltip,

    buttonText,
    onPressButton,
    submitPrimaryStep,
    canContinueResaleStep,
    resendCode,
    isValid,

    isSendingPhoneVerification,

  } = useSignup();
  const { isSubscriptionOpen } = useReality();

  const onClickBack = () => {
    if (currentStep > 1) {
      if (realityInfo.showResaleCode && currentStep === 3) {
        setCurrentStep(1);
      } else {
        setCurrentStep(currentStep - 1);
      }
      return;
    }
    navigate("/sign-in");
  };

  return (
    <AuthWrapper
      pageTitle="INSCREVA-SE AQUI!"
      useSignupImages
      onClickBack={onClickBack}
    >
      <SignupStepper
        currentStep={currentStep - 1}
        skipResaleStep={realityInfo.showResaleCode}
      />

      {/* User info step */}
      <Box display={currentStep === 1 ? "block" : "none"}>
        {realityInfo.showResaleCode ? (
          <Text fontSize="1rem" mt="1rem">
            Passo 1 - Preencha seus dados para a inscrição:
          </Text>
        ) : (
          <Text fontSize="1rem" mt="1rem">
            Preencha seus dados para a inscrição:
          </Text>
        )}

        <Spacer height="1.5rem" />
        <TextField
          label="Nome completo*"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError}
        />

        <Spacer height="1rem" />
        <TextField
          label="E-mail*"
          value={email}
          setValue={setEmail}
          error={!!emailError}
          helperText={emailError}
        />

        <Spacer height="1rem" />
        <TextField
          label="Confirme seu E-mail*"
          value={emailConfirmation}
          setValue={setEmailConfirmation}
          error={!!emailConfirmationError}
          helperText={emailConfirmationError}
        />

        <Spacer height="1rem" />
        <Box display="flex" flex={1}>
          <PhoneInput
            label="Telefone (WhatsApp)*"
            value={phoneNumber}
            setValue={skipPhoneIntro ? setPhoneNumber : undefined}
            error={!!phoneNumberError}
            errorText={phoneNumberError}
            ContainerProps={{
              flex: 1,
              ...(!skipPhoneIntro && {
                onClick: () => setIsOpenPhoneNumberDialog(true),
              }),
            }}
          />

          <Spacer width="1rem" />

          <DateInput
            label="Data de nascimento*"
            value={birthDate}
            setValue={setBirthDate}
            error={!!birthDateError}
            errorText={birthDateError}
            ContainerProps={{ flex: 1 }}
          />
        </Box>

        <Spacer height="1rem" />
        <GenderSelect
          label="Gênero*"
          value={gender}
          onChange={(e) => setGender(e.target.value as Gender)}
          ContainerProps={{ width: "100%" }}
        />

        <Spacer height="1rem" />
        <Box display="flex" flex={1}>
          <Box display="block" flex={1}>
            <StateSelect
              label="Estado*"
              value={selectedState}
              setValue={setSelectedState}
            />
          </Box>
          <Spacer width="1rem" />
          <Box display="block" flex={1}>
            <CitySelect
              label="Cidade*"
              state={selectedState}
              value={selectedCity}
              setValue={setSelectedCity}
              ContainerProps={{ width: "100%" }}
            />
          </Box>
        </Box>

        <Divider margin="1rem 0rem" />

        <Checkbox
          value={confirmWorker}
          onChange={() => setConfirmWorker(!confirmWorker)}
        >
          <Text>{realityInfo.confirmOccupationText}</Text>
        </Checkbox>

        <Checkbox
          value={confirmTerms}
          onChange={() => setConfirmTerms(!confirmTerms)}
        >
          <Text display="inline">
            Estou de acordo com o{" "}
            <a href={getTermsOfUseFile()} target="_blank" rel="noreferrer">
              Termo de Uso
            </a>{" "}
            disponível.
          </Text>
        </Checkbox>
      </Box>

      {/* resale code step */}
      <Box
        display="none"
      >
        <Text fontSize="1rem" mt="1rem">
          Passo 2 — Você possui código de revenda?
        </Text>
        <Text fontSize="0.875rem" color={Theme.colors.text.secondary} mt="0.5rem">
          Selecione uma opção para continuar. O código vincula seu perfil a uma loja parceira.
        </Text>

        <Box display="flex" gap={1.5} width="100%" mt="1.25rem" flexDirection={{ xs: "column", sm: "row" }}>
          <Button
            size="large"
            fullWidth
            variant={resaleCodeAnswer === "yes" ? "contained" : "outlined"}
            onClick={() => setResaleCodeAnswer("yes")}
          >
            Sim
          </Button>
          <Button
            size="large"
            fullWidth
            variant={resaleCodeAnswer === "no" ? "contained" : "outlined"}
            onClick={() => setResaleCodeAnswer("no")}
          >
            Não
          </Button>
        </Box>

        {resaleCodeAnswer === "yes" && (
          <>
            <Spacer height="1.5rem" />
            <MaskedInput
              label="Código de Revenda"
              value={resaleCode}
              setValue={(val) => setResaleCode(val.toUpperCase())}
              mask="a99999"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip
                      isOpen={isVisibleResaleCodeTooltip}
                      onClose={toggleIsVisibleResaleCodeTooltip}
                      placement={"bottom-end"}
                      title="O Código da Revenda relaciona o seu perfil com uma Loja de Materiais para Construção. Entre em contato com a revenda Quartzolit mais próxima e solicite este código gratuito."
                    >
                      <Box
                        display="flex"
                        style={{ cursor: "pointer" }}
                        onClick={toggleIsVisibleResaleCodeTooltip}
                      >
                        <InfoIcon size={20} />
                      </Box>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Text mt="0.75rem" color={Theme.colors.text.secondary} fontSize="0.8rem">
              Formato: uma letra seguida de 5 números (ex.: A12345).
            </Text>
          </>
        )}
      </Box>

      {/* otp verification step */}
      <Box
        display={
          (currentStep === 2 && !realityInfo.showResaleCode) ||
          (currentStep === 2 && isFinishingSignUp) ||
          currentStep === 3
            ? "flex"
            : "none"
        }
        flexDirection="column"
        alignItems="center"
      >
        <Text fontSize="1rem" mt="1rem">
          Passo 2 - Insira o código recebido abaixo:
        </Text>

        <Spacer height="1.5rem" />

        <OtpCodeInput
          value={otp}
          onChange={setOtp}
          isInvalid={isInvalidCode}
          onSubmit={onPressButton}
        />
        {isInvalidCode && (
          <>
            <Spacer height="0.2rem" />
            <Text color={Theme.colors.feedback.negative} fontSize="0.7rem">
              Código inválido ou inexistente.
            </Text>
          </>
        )}

        <Spacer height="2rem" />
        <Text color={Theme.colors.text.secondary}>
          Tempo de reenvio do código: {smsTimerValue}
        </Text>

        <Spacer height="2.5rem" />
        <ResendCodeButton
          disabled={smsTimerValue > 0 || isSendingPhoneVerification}
          onClick={resendCode}
        />
      </Box>

      <Spacer height="1.5rem" />
      <Button
        size="large"
        onClick={() => submitPrimaryStep()}
        disabled={
          !isSubscriptionOpen ||
          isLoadingButton ||
          (!isValid && currentStep === 1) ||
          (realityInfo.showResaleCode &&
            currentStep === 2 &&
            !isFinishingSignUp &&
            !canContinueResaleStep)
        }
      >
        {isLoadingButton ? (
          <CircularProgress style={{ color: "white" }} size="1rem" />
        ) : (
          buttonText
        )}
      </Button>

      {/* --- Dialogs --- */}
      <PhoneNumberDialog
        isOpen={isOpenPhoneNumberDialog}
        email={phoneNumber}
        setEmail={setPhoneNumber}
        onConfirm={onValidatePhone}
        onClose={goToSignIn}
        isLoading={isLoadingButton}
        error={phoneNumberError}
      />
      <Dialog
        isOpen={isOpenAlreadyRegistered}
        text={`Este telefone já possui cadastro!\nClique abaixo para fazer o login.`}
        confirmButtonText="Entrar"
        onConfirm={goToSignIn}
        onClose={goToWebsite}
        closeButtonText="Sair"
      />

      <Dialog
        isOpen={isOpenChooseValidation}
        text={`Escolha uma opção abaixo.`}
        confirmButtonText="Receber código por SMS"
        onConfirm={() => onSendSmsCode()}
        closeButtonText="Corrigir número"
        onClose={onFixPhoneNumber}
        isLoading={isSendingPhoneVerification}
      />
      <Dialog
        isOpen={isOpenSignUp}
        text={`Telefone verificado!\nVocê ainda não possui um cadastro. Clique abaixo em "Inscreva-se" para seguir com seu cadastro.`}
        confirmButtonText="Inscreva-se"
        onConfirm={closeSignupDialog}
        onClose={goBack}
        closeButtonText="Sair"
      />
      <Dialog
        isOpen={isOpenBlacklistedModal}
        text={realityInfo.blacklistModalText}
        confirmButtonText="Sair"
        onConfirm={goToWebsite}
      />

      <Dialog
        isOpen={isOpenFinishSignUp}
        icon={<CircleCheck />}
        text={`Telefone verificado!\nVocê possui um cadastro não finalizado. Escolha uma opção abaixo para seguir com seu cadastro.`}
        confirmButtonText="Receber código por SMS"
        onConfirm={() => onSendSmsCode()}
        closeButtonText="Corrigir número"
        onClose={onFixPhoneNumber}
        isLoading={isSendingPhoneVerification}
      />

      <div id="recaptcha-container" />
    </AuthWrapper>
  );
}
